import { Box } from '@mui/material';
import classNames from 'classnames';
import React, { memo } from 'react';

const rootSx = {
  position: 'relative',
  width: '100%',
  height: '100%',
};

const trackSx = (theme) => ({
  // Position
  position: 'absolute',
  top: '50%',
  marginTop: '-4px',
  marginLeft: '-10px',

  // Layout
  width: '100%',
  height: '8px',
  padding: '0 10px',

  boxSizing: 'content-box',
  backgroundColor: theme.tokens.matterColorNegative,
  borderRadius: '4px',
  boxShadow: 'inset 0 1px 3px 0 rgba(0, 0, 0, 0.1)',
});

const rangeSx = (theme) => ({
  position: 'absolute',
  top: '50%',
  marginTop: '-4px',
  height: '8px',
  backgroundColor: theme.tokens.marketplaceColor,
});

/**
 * Renders the slider track and the filled ranges between handles.
 *
 * @param {Object} props
 * @param {string} [props.rootClassName] - Overrides the default root class.
 * @param {string} [props.className] - Extra class names for the root element.
 * @param {React.ReactNode} [props.children] - Handles rendered on top of the track.
 * @param {number[]} [props.handles] - Handle values in ascending order.
 * @param {Function} props.valueToPosition - Maps a value to a pixel position.
 */
const TrackComponent = ({
  rootClassName = null,
  className = null,
  children = null,
  handles = [],
  valueToPosition,
}) => {
  const positionFromIndex = (index) => valueToPosition(handles[index]);

  const classes = classNames(rootClassName, className);

  return (
    <Box className={classes} sx={rootClassName ? undefined : rootSx}>
      <Box sx={trackSx} />

      {handles.reduce((ranges, h, index) => {
        return index < handles.length - 1
          ? [
              ...ranges,
              <Box
                key={`range_${index}-${index + 1}`}
                sx={(theme) => ({
                  ...rangeSx(theme),
                  left: `${valueToPosition(h)}px`,
                  width: `${positionFromIndex(index + 1) - valueToPosition(h)}px`,
                })}
              />,
            ]
          : ranges;
      }, [])}

      {children}
    </Box>
  );
};

TrackComponent.displayName = 'Track';

const Track = memo(TrackComponent);

export default Track;
